import type { AtlasBuildInfo, ReprocessJob, ReprocessJobResponse } from '@brimstone/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { EmptyState, Panel, PrimaryButton, SecondaryButton } from '../../components/ui';
import { api } from '../../lib/api';
import { queryKeys, useStatus } from '../../lib/queries';
import { formatAge } from '../../lib/time';

/** Files the server couldn't read (SPEC §4.3): path, error, when. */
export function ParseErrorsPanel() {
  const status = useStatus();
  const errors = status.data?.parseErrors ?? [];
  return (
    <Panel title="Parse errors" level="side" meta={status.data ? String(errors.length) : undefined}>
      {errors.length === 0 ? (
        <EmptyState>{status.data ? 'None: every uploaded file was read.' : 'Loading…'}</EmptyState>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-3 px-4 py-3">
          {errors.map((e) => (
            <li key={e.rawFileId} className="min-w-0">
              <div className="truncate font-mono text-secondary" title={e.relPath}>
                {e.relPath}
              </div>
              <div className="text-secondary text-danger">{e.error}</div>
              <div className="font-mono text-label text-muted">
                uploaded {formatAge(e.uploadedAt)}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

const ATLAS_STATE: Record<AtlasBuildInfo['status'], string> = {
  pending: 'Waiting',
  fetching: 'Fetching',
  deriving: 'Drawing',
  ready: 'Ready',
  error: 'Stopped, will retry',
};

/** The map atlas of each client build (MAP1): fetched from wago.tools, then drawn. */
export function AtlasPanel() {
  const status = useStatus();
  const builds = status.data?.atlas ?? [];
  return (
    <Panel title="Map atlas" level="side" meta={status.data ? String(builds.length) : undefined}>
      {builds.length === 0 ? (
        <EmptyState>
          {status.data
            ? 'No client build yet: the watcher uploads .build.info from the WoW folder.'
            : 'Loading…'}
        </EmptyState>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-3 px-4 py-3">
          {builds.map((b) => (
            <li key={b.version} className="min-w-0">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-mono text-secondary">{b.version}</span>
                <span
                  className={`font-mono text-label ${b.status === 'error' ? 'text-danger' : 'text-muted'}`}
                >
                  {ATLAS_STATE[b.status]}
                  {b.needed ? ` · ${b.stored}/${b.needed} files` : ''}
                </span>
              </div>
              <div className="font-mono text-label text-muted">
                {b.readyAt ? `ready ${formatAge(b.readyAt)}` : `seen ${formatAge(b.firstSeenAt)}`}
              </div>
              {b.errors.map((e) => (
                <div key={e} className="truncate text-secondary text-danger" title={e}>
                  {e}
                </div>
              ))}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function progress(job: ReprocessJob): string {
  if (job.status === 'error') return `Failed: ${job.error ?? 'unknown error'}`;
  if (job.status === 'done') {
    return `Done: ${job.done} files${job.errors ? `, ${job.errors} with errors` : ''}.`;
  }
  return job.total ? `${job.done} of ${job.total} files…` : 'Starting…';
}

/** Rebuilds every derived table from the raw files (SPEC §4.4). */
export function ReprocessPanel() {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);

  const start = useMutation({
    mutationFn: () =>
      api<ReprocessJobResponse>('/api/admin/reprocess', { method: 'POST', json: {} }),
    onSuccess: (res) => {
      setJobId(res.job.id);
      setConfirming(false);
    },
  });
  const job = useQuery({
    queryKey: ['reprocess', jobId],
    queryFn: () => api<ReprocessJobResponse>(`/api/admin/reprocess/${jobId}`),
    enabled: jobId !== null,
    refetchInterval: (q) => (q.state.data?.job.status === 'running' ? 1000 : false),
  });
  const current = job.data?.job ?? start.data?.job;
  const running = current?.status === 'running';
  const finished = current !== undefined && !running;
  useEffect(() => {
    // Parse errors may have changed.
    if (finished) void queryClient.invalidateQueries({ queryKey: queryKeys.status });
  }, [finished, queryClient]);

  return (
    <Panel title="Reprocess" level="side">
      <p className="m-0 px-4 pt-3 text-secondary text-muted">
        Rebuild facts, events and prices from every uploaded file. Needed after an adapter fix;
        roster flags and settings are kept.
      </p>
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        {confirming ? (
          <>
            <PrimaryButton type="button" disabled={start.isPending} onClick={() => start.mutate()}>
              Rebuild everything
            </PrimaryButton>
            <SecondaryButton type="button" onClick={() => setConfirming(false)}>
              Cancel
            </SecondaryButton>
          </>
        ) : (
          <SecondaryButton type="button" disabled={running} onClick={() => setConfirming(true)}>
            Reprocess…
          </SecondaryButton>
        )}
      </div>
      {start.isError && (
        <p className="m-0 px-4 pb-3 text-secondary text-danger">{start.error.message}</p>
      )}
      {current && (
        <p
          className={`m-0 px-4 pb-3 font-mono text-secondary ${current.status === 'error' ? 'text-danger' : 'text-muted'}`}
        >
          {progress(current)}
        </p>
      )}
    </Panel>
  );
}
